import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type ButtonProps = {
  to?: string;
  href?: string;
  variant?: "primary" | "outline" | "gold" | "ghost" | "outline-light";
  size?: "md" | "lg";
  icon?: string;
  iconLeft?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
};

export default function Button({
  to,
  href,
  variant = "primary",
  size = "md",
  icon,
  iconLeft = false,
  onClick,
  className = "",
  children,
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap cursor-pointer transition-colors duration-300 select-none";
  const sizes =
    size === "lg"
      ? "px-7 py-3.5 text-base"
      : "px-5 py-2.5 text-sm";

  const variants: Record<string, string> = {
    primary: "bg-primary text-white hover:bg-primary-dark",
    gold: "bg-gold text-white hover:bg-gold/90",
    outline: "border border-primary/30 text-primary hover:border-primary hover:bg-primary/5",
    "outline-light":
      "border border-white/40 text-white hover:border-white hover:bg-white/10",
    ghost: "text-primary hover:text-gold",
  };

  const cls = `${base} ${sizes} ${variants[variant]} ${className}`;

  const iconEl = icon ? (
    <i className={`${icon} text-base leading-none`} aria-hidden="true"></i>
  ) : null;

  const content = (
    <>
      {icon && iconLeft && iconEl}
      <span>{children}</span>
      {icon && !iconLeft && iconEl}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} onClick={onClick}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} onClick={onClick}>
        {content}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {content}
    </button>
  );
}